// The terms a customer accepts for EACH purchase (Neil, 10 Oct 2026): shown inside every order
// and renewal dialog (the large ConfirmDialog), with a tick box naming the credits this property
// uses. The tick only becomes available once the terms have been scrolled to the end, and the
// customer is told so. The API holds the wording (GET /v1/terms/purchase) and refuses a
// purchase without this tick, an older version, or different credits; what was accepted is
// recorded on the order (100-api common/terms.py).
//
// An estimated property (no metered figure) also shows HOW its electricity use is estimated: the
// standard (CIBSE TM46) and the method, inside the terms box so it's read to the end too, with its
// own tick worded by the API (Neil, 14 Oct 2026: "clearly specify what standards and methods we used
// ... and they accept that it is an estimate").
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowDown, CheckCircle2 } from "lucide-react";
import { useApi, type PurchaseTermsDoc } from "../api";

/** What the parent sends with the purchase once ticked; null until then. */
export type TermsAcceptance = { accepted: true; version: string; credits: number } | null;

// Fetched once per page load: every dialog shows the same version
let cached: Promise<PurchaseTermsDoc> | null = null;

const plural = (n: number) => `${n.toLocaleString("en-GB")} report credit${n === 1 ? "" : "s"}`;
const END_SLACK_PX = 8; // "at the end" allows for sub-pixel rounding

/** An extra acknowledgement shown in the same card as the terms tick (e.g. estimated figures). */
export type ExtraTick = { label: ReactNode; checked: boolean; onChange: (checked: boolean) => void };

export function PurchaseTerms({ credits, onChange, extra }: { credits: number; onChange: (a: TermsAcceptance) => void; extra?: ExtraTick }) {
  const api = useApi();
  const [doc, setDoc] = useState<PurchaseTermsDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ticked, setTicked] = useState(false);
  // Have they scrolled to the end of the terms? Only then can they tick
  const [read, setRead] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    cached ??= api.getPurchaseTerms();
    cached.then(setDoc).catch((e: Error) => {
      cached = null; // try again next time
      setError(e.message);
    });
  }, [api]);

  // A new property or price: the tick starts again
  useEffect(() => {
    setTicked(false);
    onChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credits]);

  /** At the end of the terms (or they all fit without scrolling)? */
  const checkEnd = useCallback(() => {
    const el = box.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - END_SLACK_PX) setRead(true);
  }, []);

  // Once the terms are on screen, and whenever the window changes size
  useEffect(() => {
    if (!doc) return;
    const t = window.setTimeout(checkEnd, 50);
    window.addEventListener("resize", checkEnd);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", checkEnd);
    };
  }, [doc, checkEnd]);

  if (error) return <p className="pterms-error">We couldn't load the terms of purchase, so this can't go ahead yet. Please try again.</p>;
  if (!doc) return <p className="muted">Loading the terms of purchase…</p>;

  return (
    <div className="pterms">
      <div ref={box} className="pterms-box" tabIndex={0} aria-label={doc.title} onScroll={checkEnd}>
        <h3>{doc.title}</h3>
        <p className="muted">{doc.intro}</p>
        <ol>
          {doc.clauses.map((c) => (
            <li key={c.heading}>
              <b>{c.heading}.</b> {c.text}
            </li>
          ))}
        </ol>
        {extra && doc.estimate && (
          <section className="pterms-estimate">
            <h3>{doc.estimate.title}</h3>
            {doc.estimate.method.map((m) => (
              <p key={m}>{m}</p>
            ))}
          </section>
        )}
        <p className="pterms-end muted">End of the terms.</p>
      </div>
      {/* Tell them why the tick box isn't available yet */}
      <p className={`pterms-hint${read ? " is-read" : ""}`} aria-live="polite">
        {read ? (
          <>
            <CheckCircle2 size={15} aria-hidden="true" /> Thanks, you've read the terms. Tick {extra ? 'both boxes' : 'the box'} below to accept them for this property.
          </>
        ) : (
          <>
            <ArrowDown size={15} aria-hidden="true" /> Please scroll to the end of the terms to accept them.
          </>
        )}
      </p>
      {/* Every acknowledgement for this purchase in one card, all unlocked by reading to the end */}
      <div className={`pterms-accept${read ? "" : " is-locked"}`}>
        {extra && (
          <label>
            <input type="checkbox" checked={extra.checked} disabled={!read} onChange={(e) => extra.onChange(e.target.checked)} />
            <span>{doc.estimate?.statement ?? extra.label}</span>
          </label>
        )}
        <label>
          <input
            type="checkbox"
            checked={ticked}
            disabled={!read}
            onChange={(e) => {
              setTicked(e.target.checked);
              onChange(e.target.checked ? { accepted: true, version: doc.version, credits } : null);
            }}
          />
          <span>{doc.statement.replace("{credits}", plural(credits))}</span>
        </label>
      </div>
    </div>
  );
}
